import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const addItem = useCallback((product, size, extra = {}) => {
    setItems((prev) => {
      const key = `${product.id}-${size || ""}${extra.bundle_id ? `-b${extra.bundle_id}` : ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          title: product.title,
          price: product.effective_price ?? product.price,
          size: size || "",
          image: product.image,
          quantity: 1,
          bundle_id: extra.bundle_id || null,
          bundle_title: extra.bundle_title || null,
          bundle_product_ids: extra.bundle_product_ids || null,
          bundle_factor: extra.bundle_factor || null,
        },
      ];
    });
  }, [setItems]);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, [setItems]);

  const updateQty = useCallback((key, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, [setItems]);

  const clear = useCallback(() => setItems([]), [setItems]);

  // Bundle price only holds while every product of the bundle is still in the cart.
  const pricedItems = useMemo(() => items.map((i) => {
    if (!i.bundle_id) return { ...i, regular_price: i.price };
    const inCart = new Set(items.filter((o) => o.bundle_id === i.bundle_id).map((o) => o.product_id));
    const complete = (i.bundle_product_ids || []).every((pid) => inCart.has(pid));
    const price = complete && i.bundle_factor ? Math.round(i.price * i.bundle_factor * 100) / 100 : i.price;
    return { ...i, regular_price: i.price, price, bundle_active: complete };
  }), [items]);

  const total = useMemo(
    () => pricedItems.reduce((s, i) => s + i.price * i.quantity, 0),
    [pricedItems]
  );
  const count = useMemo(
    () => items.reduce((s, i) => s + i.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({ items: pricedItems, open, setOpen, addItem, removeItem, updateQty, clear, total, count }),
    [pricedItems, open, setOpen, addItem, removeItem, updateQty, clear, total, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
